import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ColumnSelect } from './ui/select';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Phone, MessageCircle, CheckCircle2, AlertCircle, Clock, XCircle, RotateCcw, User, Search, ChevronDown, ChevronLeft, ArrowLeft, BarChart3, Repeat, Zap, CheckCheck, ArrowRight, Calendar, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const STATUS_FILTER_OPTIONS = ['Todos', 'Pendiente', 'Enviado', 'Entregado', 'Leído', 'Fallido', 'Límite Excedido'] as const;
const STATUS_LABEL_TO_VALUE: Record<string, string> = {
  Todos: '',
  Pendiente: 'pending',
  Enviado: 'sent',
  Entregado: 'delivered',
  Leído: 'read',
  Fallido: 'failed',
  'Límite Excedido': 'blocked',
};
const STATUS_VALUE_TO_LABEL: Record<string, string> = {
  pending: 'Pendiente',
  sent: 'Enviado',
  delivered: 'Entregado',
  read: 'Leído',
  failed: 'Fallido',
  blocked: 'Límite Excedido',
};

const DATE_FIELD_OPTIONS = ['Creación', 'Programación', 'Envío', 'Entrega'] as const;
const DATE_FIELD_LABEL_TO_VALUE: Record<string, string> = {
  Creación: 'created_at',
  Programación: 'scheduled_at',
  Envío: 'sent_at',
  Entrega: 'delivered_at',
};
const DATE_FIELD_VALUE_TO_LABEL: Record<string, string> = {
  created_at: 'Creación',
  scheduled_at: 'Programación',
  sent_at: 'Envío',
  delivered_at: 'Entrega',
};

const DATE_INPUT_CLASS =
  'rounded-md bg-white py-1.5 px-3 text-sm shadow-sm border border-gray-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]';

interface MessageReport {
  id: string;
  phone_number: string;
  message_content: string;
  status: string;
  delivery_status?: string;
  scheduled_at?: string;
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  created_at: string;
  campaign_id?: string;
  campaign_name?: string;
  campaign_type?: string;
  customer_name?: string;
  error?: string;
  error_code?: number;
  template_name?: string;
}

interface CampaignSummary {
  id: string;
  name: string;
  type: string;
  created_at?: string;
  total_messages: number;
  sent_messages: number;
  pending_messages: number;
  read_messages: number;
  failed_messages: number;
}

interface SelectedCampaign {
  id: string;
  name: string;
  type: string;
}

const Report: React.FC = () => {
  const { user, token } = useAuth();
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<CampaignSummary[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<SelectedCampaign | null>(null);
  const [campaignMessages, setCampaignMessages] = useState<MessageReport[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [campaignTotalSent, setCampaignTotalSent] = useState<number>(0);
  const [campaignTotalPending, setCampaignTotalPending] = useState<number>(0);
  const [campaignTotalRead, setCampaignTotalRead] = useState<number>(0);
  const [campaignTotalFailed, setCampaignTotalFailed] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const campaignsItemsPerPage = 12;
  const messagesItemsPerPage = 50;
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [campaignTypeFilter, setCampaignTypeFilter] = useState<string>('');
  const [showTypeDropdown, setShowTypeDropdown] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [dateField, setDateField] = useState<string>('created_at');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  useEffect(() => {
    if (user && token) {
      loadCampaigns();
    }
  }, [user, token, debouncedSearchTerm, campaignTypeFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (selectedCampaign && user && token) {
      loadCampaignMessages();
    }
  }, [selectedCampaign, user, token, currentPage, debouncedSearchTerm, statusFilter, dateField, dateFrom, dateTo]);

  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (debouncedSearchTerm && debouncedSearchTerm.length >= 3) {
        params.search = debouncedSearchTerm;
      }
      if (campaignTypeFilter) {
        params.campaign_type = campaignTypeFilter;
      }

      const response = await axios.get(`${API}/campaigns/by-type`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data.success) {
        const allCampaigns = response.data.campaigns || [];
        setCampaigns(allCampaigns);
        const start = (currentPage - 1) * campaignsItemsPerPage;
        const end = start + campaignsItemsPerPage;
        setFilteredCampaigns(allCampaigns.slice(start, end));
      } else {
        toast.error('Error al cargar las campañas');
      }
    } catch (error: any) {
      console.error('Error loading campaigns:', error);
      toast.error('Error al cargar las campañas', {
        description: error.response?.data?.detail || 'Ocurrió un error inesperado',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Re-paginate when campaigns or page changes (campaign cards)
    const start = (currentPage - 1) * campaignsItemsPerPage;
    const end = start + campaignsItemsPerPage;
    setFilteredCampaigns(campaigns.slice(start, end));
  }, [campaigns, currentPage]);

  const loadCampaignMessages = async () => {
    if (!selectedCampaign) return;

    try {
      setMessagesLoading(true);
      const skip = (currentPage - 1) * messagesItemsPerPage;
      const params: any = {
        limit: messagesItemsPerPage,
        skip,
        date_field: dateField || 'created_at',
      };
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }

      const response = await axios.get(`${API}/campaigns/${selectedCampaign.id}/messages/detailed`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      if (response.data.success) {
        setCampaignMessages(response.data.messages || []);
        setTotal(response.data.total || 0);
        setCampaignTotalSent(response.data.total_sent ?? 0);
        setCampaignTotalPending(response.data.total_pending ?? 0);
        setCampaignTotalRead(response.data.total_read ?? 0);
        setCampaignTotalFailed(response.data.total_failed ?? 0);
      } else {
        toast.error('Error al cargar los mensajes');
      }
    } catch (error: any) {
      console.error('Error loading campaign messages:', error);
      toast.error('Error al cargar los mensajes', {
        description: error.response?.data?.detail || 'Ocurrió un error inesperado',
      });
    } finally {
      setMessagesLoading(false);
    }
  };

  const getDeliveryStatusConfig = (deliveryStatus?: string, status?: string) => {
    // Check status first for failed/blocked, then delivery_status
    if (status) {
      if (status.toLowerCase() === 'failed') {
        return {
          label: 'Fallido',
          color: 'bg-red-100 text-red-700 border-red-200',
          icon: <XCircle className="h-4 w-4" />,
        };
      }
      if (status.toLowerCase() === 'blocked') {
        return {
          label: 'Límite Excedido',
          color: 'bg-orange-100 text-orange-700 border-orange-200',
          icon: <AlertCircle className="h-4 w-4" />,
        };
      }
    }

    if (!deliveryStatus) {
      // If no delivery_status, fall back to pending status
      if (status && status.toLowerCase() === 'pending') {
        return {
          label: 'Pendiente',
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          icon: <Clock className="h-4 w-4" />,
        };
      }
      return {
        label: 'Sin estado',
        color: 'bg-gray-100 text-gray-700 border-gray-200',
        icon: <AlertCircle className="h-4 w-4" />,
      };
    }

    switch (deliveryStatus.toLowerCase()) {
      case 'sent':
        return {
          label: 'Enviado',
          color: 'bg-blue-100 text-blue-700 border-blue-200',
          icon: <CheckCircle2 className="h-4 w-4" />,
        };
      case 'delivered':
        return {
          label: 'Entregado',
          color: 'bg-green-100 text-green-700 border-green-200',
          icon: <CheckCircle2 className="h-4 w-4" />,
        };
      case 'read':
        return {
          label: 'Leído',
          color: 'bg-emerald-100 text-emerald-700 border-emerald-200',
          icon: <CheckCircle2 className="h-4 w-4" />,
        };
      case 'failed':
        return {
          label: 'Fallido',
          color: 'bg-red-100 text-red-700 border-red-200',
          icon: <XCircle className="h-4 w-4" />,
        };
      default:
        return {
          label: deliveryStatus || 'Desconocido',
          color: 'bg-gray-100 text-gray-700 border-gray-200',
          icon: <AlertCircle className="h-4 w-4" />,
        };
    }
  };

  const getStatusConfig = (status?: string) => {
    if (!status) {
      return {
        label: 'Desconocido',
        color: 'bg-gray-100 text-gray-700 border-gray-200',
        icon: <AlertCircle className="h-4 w-4" />,
      };
    }

    switch (status.toLowerCase()) {
      case 'sent':
        return {
          label: 'Enviado',
          color: 'bg-blue-100 text-blue-700 border-blue-200',
          icon: <CheckCircle2 className="h-4 w-4" />,
        };
      case 'delivered':
        return {
          label: 'Entregado',
          color: 'bg-green-100 text-green-700 border-green-200',
          icon: <CheckCircle2 className="h-4 w-4" />,
        };
      case 'pending':
        return {
          label: 'Pendiente',
          color: 'bg-yellow-100 text-yellow-700 border-yellow-200',
          icon: <Clock className="h-4 w-4" />,
        };
      case 'failed':
        return {
          label: 'Fallido',
          color: 'bg-red-100 text-red-700 border-red-200',
          icon: <XCircle className="h-4 w-4" />,
        };
      default:
        return {
          label: status || 'Desconocido',
          color: 'bg-gray-100 text-gray-700 border-gray-200',
          icon: <AlertCircle className="h-4 w-4" />,
        };
    }
  };

  const formatPhoneNumber = (phone?: string): string => {
    if (!phone) return 'N/A';
    // Formatear número de teléfono para mejor legibilidad
    return phone.replace(/(\d{2})(\d{3})(\d{3})(\d{4})/, '+$1 $2 $3 $4');
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    if (selectedCampaign) {
      loadCampaignMessages();
    } else {
      loadCampaigns();
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (type: string) => {
    setCampaignTypeFilter(type);
    setShowTypeDropdown(false);
    setCurrentPage(1);
  };

  const handleSelectCampaign = (campaign: CampaignSummary) => {
    setSelectedCampaign({
      id: campaign.id,
      name: campaign.name,
      type: campaign.type
    });
    setCurrentPage(1);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setDateField('created_at');
    setDateFrom('');
    setDateTo('');
  };

  const handleBackToCampaigns = () => {
    setSelectedCampaign(null);
    setCampaignMessages([]);
    setTotal(0);
    setCampaignTotalSent(0);
    setCampaignTotalPending(0);
    setCampaignTotalRead(0);
    setCampaignTotalFailed(0);
    setCurrentPage(1);
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilter('');
    setDateField('created_at');
    setDateFrom('');
    setDateTo('');
  };

  const handleStatusFilterChange = (label: string) => {
    setStatusFilter(STATUS_LABEL_TO_VALUE[label] ?? '');
    setCurrentPage(1);
  };

  const handleDateFieldChange = (label: string) => {
    setDateField(DATE_FIELD_LABEL_TO_VALUE[label] ?? 'created_at');
    setCurrentPage(1);
  };

  const handleDateFromChange = (value: string) => {
    setDateFrom(value);
    setCurrentPage(1);
  };

  const handleDateToChange = (value: string) => {
    setDateTo(value);
    setCurrentPage(1);
  };

  const clearMessageFilters = () => {
    setStatusFilter('');
    setDateField('created_at');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  const hasActiveMessageFilters = Boolean(statusFilter || dateFrom || dateTo);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Calculate total stats from all campaigns
  const totalMessages = campaigns.reduce((sum, c) => sum + c.total_messages, 0);
  const totalSent = selectedCampaign
    ? campaignTotalSent
    : campaigns.reduce((sum, c) => sum + c.sent_messages, 0);
  const totalPending = selectedCampaign
    ? campaignTotalPending
    : campaigns.reduce((sum, c) => sum + c.pending_messages, 0);
  const totalRead = selectedCampaign
    ? campaignTotalRead
    : campaigns.reduce((sum, c) => sum + c.read_messages, 0);
  const totalFailed = selectedCampaign
    ? campaignTotalFailed
    : campaigns.reduce((sum, c) => sum + (c.failed_messages ?? 0), 0);

  // Pagination: different for campaigns list vs messages table
  const campaignsTotalPages = Math.ceil(campaigns.length / campaignsItemsPerPage);
  const messagesTotalPages = Math.ceil(total / messagesItemsPerPage);

  const getCampaignTypeIcon = (type: string) => {
    switch (type) {
      case 'marketing':
        return <BarChart3 className="h-6 w-6 text-blue-600" />;
      case 'renovacion':
        return <Repeat className="h-6 w-6 text-green-600" />;
      case 'dynamic':
        return <Zap className="h-6 w-6 text-purple-600" />;
      case 'calendar':
        return <Calendar className="h-6 w-6 text-teal-600" />;
      default:
        return <MessageCircle className="h-6 w-6 text-gray-600" />;
    }
  };

  const getCampaignTypeBg = (type: string) => {
    switch (type) {
      case 'marketing':
        return 'bg-blue-100';
      case 'renovacion':
        return 'bg-green-100';
      case 'dynamic':
        return 'bg-purple-100';
      case 'calendar':
        return 'bg-teal-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getCampaignTypeLabel = (type: string) => {
    switch (type) {
      case 'marketing':
        return 'Marketing';
      case 'renovacion':
        return 'Renovación';
      case 'dynamic':
        return 'Dinámica';
      case 'calendar':
        return 'Calendario';
      default:
        return type;
    }
  };

  // Zona horaria: Bogotá, Colombia (UTC-5)
  const TIMEZONE_BOGOTA = 'America/Bogota';

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: TIMEZONE_BOGOTA,
    });
  };

  const formatTableDate = (dateString?: string): string => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: TIMEZONE_BOGOTA,
    });
  };

  const formatScheduledAt = (message: MessageReport): string => {
    const alreadySent =
      !!message.sent_at ||
      ['sent', 'delivered', 'read'].includes(message.delivery_status || '') ||
      message.status === 'sent';
    if (alreadySent) return 'Enviado';
    return formatTableDate(message.scheduled_at);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="text-left">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reporte de Mensajes</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1 leading-relaxed">
            Historial completo de mensajes enviados desde la aplicación
          </p>
        </div>
        <div className="flex justify-start sm:justify-end items-center gap-3">
          <Button
            onClick={handleRefresh}
            disabled={loading}
            variant="outline"
            className="bg-white hover:bg-slate-50 border-gray-200 text-gray-500 w-11 h-11 p-0 rounded-xl shadow-sm transition-all active:scale-95 group"
            title="Actualizar"
          >
            <RotateCcw className={`w-5 h-5 transition-transform duration-500 ${loading ? 'animate-spin' : 'group-hover:rotate-180'}`} />
          </Button>
        </div>
      </div>

      {/* Search and Filter Bar - Show when no campaign is selected */}
      {!selectedCampaign && (
        <Card className="border-gray-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar por nombre de campaña..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="relative">
                <button
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-w-[160px] justify-between w-full sm:w-auto"
                >
                  <span>
                    {campaignTypeFilter === 'marketing' ? 'Marketing' :
                      campaignTypeFilter === 'renovacion' ? 'Renovación' :
                        campaignTypeFilter === 'dynamic' ? 'Dinámica' :
                          campaignTypeFilter === 'calendar' ? 'Calendario' :
                            'Todos los tipos'}
                  </span>
                  <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showTypeDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowTypeDropdown(false)}
                    />
                    <div className="absolute right-0 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                      <button
                        onClick={() => handleTypeFilterChange('')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg ${!campaignTypeFilter ? 'bg-gray-50 font-medium text-[#1CB3B3]' : ''
                          }`}
                      >
                        Todos los tipos
                      </button>
                      <button
                        onClick={() => handleTypeFilterChange('marketing')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${campaignTypeFilter === 'marketing' ? 'bg-gray-50 font-medium' : ''
                          }`}
                      >
                        Marketing
                      </button>
                      <button
                        onClick={() => handleTypeFilterChange('renovacion')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${campaignTypeFilter === 'renovacion' ? 'bg-gray-50 font-medium' : ''
                          }`}
                      >
                        Renovación
                      </button>
                      <button
                        onClick={() => handleTypeFilterChange('dynamic')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${campaignTypeFilter === 'dynamic' ? 'bg-gray-50 font-medium' : ''
                          }`}
                      >
                        Dinámica
                      </button>
                      <button
                        onClick={() => handleTypeFilterChange('calendar')}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 last:rounded-b-lg ${campaignTypeFilter === 'calendar' ? 'bg-gray-50 font-medium text-teal-600' : ''
                          }`}
                      >
                        Calendario
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Bar - Only show when campaign is selected */}
      {selectedCampaign && (
        <Card className="border-gray-200">
          <CardContent className="pt-6 space-y-4">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 relative min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">Buscar</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    placeholder="Buscar por número de teléfono o destinatario..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full pl-10 pr-4 py-1.5 border border-gray-200 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] text-sm"
                  />
                </div>
              </div>
              <div className="w-full lg:w-44">
                <ColumnSelect
                  label="Estado"
                  options={[...STATUS_FILTER_OPTIONS]}
                  value={STATUS_VALUE_TO_LABEL[statusFilter] || 'Todos'}
                  onChange={handleStatusFilterChange}
                  placeholder="Todos"
                />
              </div>
              <div className="w-full lg:w-40">
                <ColumnSelect
                  label="Campo de fecha"
                  options={[...DATE_FIELD_OPTIONS]}
                  value={DATE_FIELD_VALUE_TO_LABEL[dateField] || 'Creación'}
                  onChange={handleDateFieldChange}
                  placeholder="Creación"
                />
              </div>
              <div className="w-full lg:w-40">
                <label className="block text-sm font-medium text-gray-700 mb-2">Desde</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => handleDateFromChange(e.target.value)}
                  className={`w-full ${DATE_INPUT_CLASS}`}
                />
              </div>
              <div className="w-full lg:w-40">
                <label className="block text-sm font-medium text-gray-700 mb-2">Hasta</label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => handleDateToChange(e.target.value)}
                  className={`w-full ${DATE_INPUT_CLASS}`}
                />
              </div>
            </div>
            {hasActiveMessageFilters && (
              <div className="flex flex-wrap items-center gap-2">
                {statusFilter && (
                  <button
                    type="button"
                    onClick={() => handleStatusFilterChange('Todos')}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#E8F7F7] text-[#0D7377] border border-[#1CB3B3]/30"
                  >
                    Estado: {STATUS_VALUE_TO_LABEL[statusFilter]}
                    <X className="h-3 w-3" />
                  </button>
                )}
                {(dateFrom || dateTo) && (
                  <button
                    type="button"
                    onClick={() => {
                      setDateFrom('');
                      setDateTo('');
                      setCurrentPage(1);
                    }}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-[#E8F7F7] text-[#0D7377] border border-[#1CB3B3]/30"
                  >
                    {DATE_FIELD_VALUE_TO_LABEL[dateField] || 'Creación'}
                    {': '}
                    {dateFrom || '…'} — {dateTo || '…'}
                    <X className="h-3 w-3" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={clearMessageFilters}
                  className="text-xs font-medium text-[#1CB3B3] hover:text-[#189999]"
                >
                  Limpiar filtros
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
        <Card className="hover:shadow-lg transition">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total de Mensajes</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {selectedCampaign ? total : totalMessages}
                </p>
              </div>
              <div className="w-10 h-10 bg-[#E8F7F7] rounded-lg flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-[#1CB3B3]" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`hover:shadow-lg transition ${selectedCampaign ? 'cursor-pointer' : ''} ${statusFilter === 'sent' ? 'ring-2 ring-[#1CB3B3]' : ''}`}
          onClick={() => {
            if (!selectedCampaign) return;
            handleStatusFilterChange(statusFilter === 'sent' ? 'Todos' : 'Enviado');
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Enviados</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">
                  {totalSent}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-blue-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`hover:shadow-lg transition ${selectedCampaign ? 'cursor-pointer' : ''} ${statusFilter === 'pending' ? 'ring-2 ring-[#1CB3B3]' : ''}`}
          onClick={() => {
            if (!selectedCampaign) return;
            handleStatusFilterChange(statusFilter === 'pending' ? 'Todos' : 'Pendiente');
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Pendientes</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">
                  {totalPending}
                </p>
              </div>
              <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`hover:shadow-lg transition ${selectedCampaign ? 'cursor-pointer' : ''} ${statusFilter === 'read' ? 'ring-2 ring-[#1CB3B3]' : ''}`}
          onClick={() => {
            if (!selectedCampaign) return;
            handleStatusFilterChange(statusFilter === 'read' ? 'Todos' : 'Leído');
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Leídos</p>
                <p className="text-2xl font-bold text-emerald-600 mt-1">
                  {totalRead}
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCheck className="h-6 w-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card
          className={`hover:shadow-lg transition ${selectedCampaign ? 'cursor-pointer' : ''} ${statusFilter === 'failed' ? 'ring-2 ring-[#1CB3B3]' : ''}`}
          onClick={() => {
            if (!selectedCampaign) return;
            handleStatusFilterChange(statusFilter === 'failed' ? 'Todos' : 'Fallido');
          }}
        >
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Fallidos</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                  {totalFailed}
                </p>
              </div>
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaign Selection or Detail View */}
      {selectedCampaign ? (
        // Campaign Detail View with Table
        <>
          {/* Campaign Header */}
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              onClick={handleBackToCampaigns}
              className="p-2"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedCampaign.name}</h2>
              <p className="text-sm text-gray-600 mt-1">
                Campaña de {selectedCampaign.type === 'marketing' ? 'Marketing' : selectedCampaign.type === 'renovacion' ? 'Renovación' : 'Dinámica'}
              </p>
            </div>
          </div>

          {/* Messages Table */}
          {messagesLoading && campaignMessages.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-[#1CB3B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando mensajes...</p>
              </div>
            </div>
          ) : campaignMessages.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay mensajes</h3>
                <p className="text-gray-600">Esta campaña aún no tiene mensajes.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <Card className="border-gray-200">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">NÚMERO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DESTINATARIO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ESTADO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CREADO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PROGRAMADO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ENVIADO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ENTREGADO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LEÍDO</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">MENSAJE</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {campaignMessages.map((message) => {
                          const deliveryStatusConfig = getDeliveryStatusConfig(message.delivery_status, message.status);
                          return (
                            <tr key={message.id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 whitespace-nowrap text-sm font-mono text-gray-900">
                                {formatPhoneNumber(message.phone_number)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                {message.customer_name || '—'}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap">
                                <Badge
                                  variant="secondary"
                                  className={`${deliveryStatusConfig.color} border flex items-center gap-1 w-fit`}
                                >
                                  {deliveryStatusConfig.icon}
                                  {deliveryStatusConfig.label}
                                </Badge>
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatTableDate(message.created_at)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatScheduledAt(message)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatTableDate(message.sent_at)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatTableDate(message.delivered_at)}
                              </td>
                              <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                                {formatTableDate(message.read_at)}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                                {message.message_content || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Pagination */}
              {messagesTotalPages > 1 && (
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Mostrando {((currentPage - 1) * messagesItemsPerPage) + 1} - {Math.min(currentPage * messagesItemsPerPage, total)} de {total} mensajes
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || messagesLoading}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Anterior</span>
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, messagesTotalPages) }, (_, i) => {
                        let pageNum: number;
                        if (messagesTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= messagesTotalPages - 2) {
                          pageNum = messagesTotalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={messagesLoading}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                ? 'bg-[#1CB3B3] text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === messagesTotalPages || messagesLoading}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <span>Siguiente</span>
                      <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        // Campaign Cards Grid View
        <>
          {loading && filteredCampaigns.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-[#1CB3B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Cargando campañas...</p>
              </div>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No hay campañas</h3>
                <p className="text-gray-600">No se encontraron campañas con los filtros aplicados.</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {filteredCampaigns.map((campaign) => (
                  <Card
                    key={campaign.id}
                    className="border-gray-200 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                    onClick={() => handleSelectCampaign(campaign)}
                  >
                    <CardHeader className="pb-4 overflow-hidden">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-12 h-12 ${getCampaignTypeBg(campaign.type)} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          {getCampaignTypeIcon(campaign.type)}
                        </div>
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <CardTitle className="text-lg font-bold text-gray-900 truncate" title={campaign.name}>
                            {campaign.name}
                          </CardTitle>
                          <p className="text-sm text-gray-500 mt-0.5 truncate">
                            {getCampaignTypeLabel(campaign.type)}
                          </p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-[#1CB3B3] transition-colors flex-shrink-0" />
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      {/* Statistics Grid */}
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {/* Total - Left */}
                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                          <p className="text-xs text-gray-600 mb-1">Total</p>
                          <p className="text-2xl font-bold text-gray-900">{campaign.total_messages}</p>
                        </div>

                        {/* Enviados - Top Right */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-xs text-blue-700 mb-1">Enviados</p>
                          <p className="text-2xl font-bold text-blue-600">{campaign.sent_messages}</p>
                        </div>

                        {/* Pendientes - Bottom Left */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <p className="text-xs text-yellow-700 mb-1">Pendientes</p>
                          <p className="text-2xl font-bold text-yellow-600">{campaign.pending_messages}</p>
                        </div>

                        {/* Leídos - Bottom Right */}
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                          <p className="text-xs text-green-700 mb-1">Leídos</p>
                          <p className="text-2xl font-bold text-green-600">{campaign.read_messages}</p>
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          <span>Creada: {formatDate(campaign.created_at)}</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectCampaign(campaign);
                          }}
                          className="text-sm font-medium text-[#1CB3B3] hover:text-[#189999] transition-colors flex items-center gap-1"
                        >
                          Ver detalles
                          <ArrowRight className="h-3 w-3" />
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Pagination */}
              {campaignsTotalPages > 1 && (
                <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                  <p className="text-sm text-gray-600">
                    Mostrando {((currentPage - 1) * campaignsItemsPerPage) + 1} - {Math.min(currentPage * campaignsItemsPerPage, campaigns.length)} de {campaigns.length} campañas
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span>Anterior</span>
                    </button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, campaignsTotalPages) }, (_, i) => {
                        let pageNum: number;
                        if (campaignsTotalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= campaignsTotalPages - 2) {
                          pageNum = campaignsTotalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        return (
                          <button
                            key={pageNum}
                            onClick={() => handlePageChange(pageNum)}
                            disabled={loading}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                                ? 'bg-[#1CB3B3] text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                    </div>
                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === campaignsTotalPages || loading}
                      className="px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <span>Siguiente</span>
                      <ChevronDown className="h-4 w-4 rotate-[-90deg]" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default Report;
